import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { FieldErrors, useFormContext } from 'react-hook-form'
import { Box, Button, Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'

import { Patient, PatientIdentityStatus, UpdatePatientDto } from '@/core/models/patient.model'
import { useGetPatientQuery } from '@/core/services/patient'
import { useIdentityStatus } from '@/entities/patient'
import { PatientProfileForm, PatientProfileFormData } from '@/features/patient/patient-profile-form'
import { MAIN_HEADER_HEIGHT } from '@/layouts/main'
import { useIsIdentityVigilanceOfficer } from '@/shared/model/useIsIdentityVigilanceOfficer'

import { usePatientInfo } from '../model/usePatientInfo'

type PatientInfoProps = {
  errors?: FieldErrors<PatientProfileFormData>
  patientId: string
  patient?: Patient
  isOffline?: boolean
  isUpdating?: boolean
  onUpdate?: (data: UpdatePatientDto) => void
}

export function PatientInfo({ errors, patientId, isOffline = false, isUpdating, onUpdate, patient: patientProp }: PatientInfoProps) {
  const { data: patient } = useGetPatientQuery(patientId)

  const { identityStatus } = useIdentityStatus(patientId)
  const { initialValues } = usePatientInfo({ patient: patientProp ?? patient })
  const isIdentityVigilanceOfficer = useIsIdentityVigilanceOfficer()

  const isReferenceIdentityLocked = useMemo(() => {
    if (isIdentityVigilanceOfficer) return false
    return identityStatus === PatientIdentityStatus.QUALIFIED || identityStatus === PatientIdentityStatus.RECOVERED
  }, [identityStatus, isIdentityVigilanceOfficer])

  const [editable, setEditable] = useState(false)

  return (
    <div>
      <PatientProfileForm
        action={({ isSubmitting }) => <FormActions editable={editable} isSubmitting={isUpdating ?? isSubmitting} setEditable={setEditable} />}
        actionPlacement="top"
        editable={editable}
        errors={errors}
        identityStatus={identityStatus}
        initialValues={initialValues}
        isReferenceIdentityLocked={isReferenceIdentityLocked}
        mode="edit"
        onUpdate={isOffline ? onUpdate : undefined}
        patientId={patientId}
        savedAddresses={(patientProp ?? patient)?.savedAddresses}
        setEditable={setEditable}
      />
    </div>
  )
}

function FormActions({
  editable,
  isSubmitting,
  setEditable,
}: {
  canModify?: boolean
  editable: boolean
  isSubmitting: boolean
  setEditable: Dispatch<SetStateAction<boolean>>
}) {
  useLingui()
  const rhForm = useFormContext<PatientProfileFormData>()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [isSticky, setIsSticky] = useState(false)

  useEffect(() => {
    function onScroll() {
      const wrapper = wrapperRef.current
      if (!wrapper) return
      setIsSticky(wrapper.getBoundingClientRect().top <= MAIN_HEADER_HEIGHT)
    }

    window.addEventListener('scroll', onScroll)

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  return (
    <Stack
      ref={wrapperRef}
      direction="row"
      id="update-button"
      justifyContent="end"
      spacing={2}
      sx={[
        { mt: '-20px', mx: '-16px', p: '16px', position: 'sticky', top: `${MAIN_HEADER_HEIGHT}px`, zIndex: 10 },
        isSticky && {
          background: 'white',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        },
      ]}
    >
      {editable ? (
        <>
          {isSticky && <Box position="absolute" sx={{ width: '100vw', height: '100%' }} />}
          <LoadingButton disableElevation loading={isSubmitting} type="submit" variant="contained">
            <Trans>Submit</Trans>
          </LoadingButton>
          <Button
            onClick={() => {
              rhForm.reset()
              setEditable(false)
            }}
            variant="neutral"
          >
            <Trans>Cancel</Trans>
          </Button>
        </>
      ) : (
        <Button onClick={() => setEditable(true)} variant="contained" color="primary">
          <Trans>Update</Trans>
        </Button>
      )}
    </Stack>
  )
}
